/**
 * RCS Template Creator — inline modal so operators never leave the
 * bot studio to build a template. POSTs to Deola's /templates/
 * endpoint (same one the Templates page uses) with the shape the
 * Twilio Content API expects for RCS.
 *
 * Covers the 4 most-used template shapes:
 *   • Text-only — a plain message with variables
 *   • Rich card — title + description + image + optional URL button
 *   • Menu     — text + suggested_replies chips (up to 11)
 *   • Carousel — 2-10 cards, each with title + description + image
 *                + one QUICK_REPLY button (postback_data)
 *
 * On successful create, calls onCreated with the returned template so
 * the picker can auto-select it. The template is auto-approved by
 * Twilio (RCS doesn't have WhatsApp-style approval).
 */
import React, { useEffect, useState } from "react";
import { X, Plus, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import useRequest from "../../../hook/use-req";
import type { TemplateModule } from "../../../../views/marketing-pages/templates/types";

type TemplateShape = "text" | "rich_card" | "menu" | "carousel";

interface Chip { text: string; postback_data: string; }
interface CardDraft {
  title: string;
  description: string;
  media_url: string;
  chip_text: string;
  chip_postback: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  agentId: string;                      // e.g. "dalmlabs_connect" — from tenant lookup, may be empty
  onCreated: (tpl: TemplateModule) => void;
}

export const RcsTemplateCreator: React.FC<Props> = ({ open, onClose, agentId: agentIdProp, onCreated }) => {
  // Local editable copy — the lookup pre-fills, but operator can
  // override or fill in manually if the tenant hasn't been provisioned
  // yet (or the lookup endpoint isn't reachable in this env).
  const [agentId, setAgentId] = useState(agentIdProp || "");
  useEffect(() => { if (agentIdProp) setAgentId(agentIdProp); }, [agentIdProp]);
  const token = localStorage.getItem("authToken");
  const { makeRequest: createTemplate } = useRequest("/templates/", "POST", {
    Authorization: `Bearer ${token}`,
  });

  const [shape, setShape] = useState<TemplateShape>("text");
  const [name, setName] = useState("");
  const [category, setCategory] = useState<"UTILITY" | "MARKETING" | "AUTHENTICATION">("MARKETING");
  const [bodyText, setBodyText] = useState("");
  const [cardTitle, setCardTitle] = useState("");
  const [cardDesc, setCardDesc] = useState("");
  const [cardImage, setCardImage] = useState("");
  const [cardButtonText, setCardButtonText] = useState("");
  const [cardButtonUrl, setCardButtonUrl] = useState("");
  const [chips, setChips] = useState<Chip[]>([{ text: "Yes", postback_data: "YES" }, { text: "No", postback_data: "NO" }]);
  const [cards, setCards] = useState<CardDraft[]>([
    { title: "", description: "", media_url: "", chip_text: "Select", chip_postback: "SELECT_1" },
    { title: "", description: "", media_url: "", chip_text: "Select", chip_postback: "SELECT_2" },
  ]);
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const reset = () => {
    setShape("text"); setName(""); setCategory("MARKETING");
    setBodyText(""); setCardTitle(""); setCardDesc(""); setCardImage("");
    setCardButtonText(""); setCardButtonUrl("");
    setChips([{ text: "Yes", postback_data: "YES" }, { text: "No", postback_data: "NO" }]);
    setCards([
      { title: "", description: "", media_url: "", chip_text: "Select", chip_postback: "SELECT_1" },
      { title: "", description: "", media_url: "", chip_text: "Select", chip_postback: "SELECT_2" },
    ]);
  };

  const buildComponents = () => {
    switch (shape) {
      case "text":
        return [{ type: "text", text: bodyText.trim() || "Hello!" }];
      case "rich_card": {
        const c: any = { type: "rich_card", title: cardTitle.trim() || "Untitled", description: cardDesc.trim() };
        if (cardImage.trim()) c.media_url = cardImage.trim();
        if (cardButtonText.trim() && cardButtonUrl.trim()) {
          c.actions = [{ type: "URL", text: cardButtonText.trim(), url: cardButtonUrl.trim() }];
        }
        const list: any[] = [];
        if (bodyText.trim()) list.push({ type: "text", text: bodyText.trim() });
        list.push(c);
        return list;
      }
      case "menu": {
        const list: any[] = [];
        if (bodyText.trim()) list.push({ type: "text", text: bodyText.trim() });
        list.push({
          type: "suggested_replies",
          text: bodyText.trim() || "Pick one:",
          replies: chips
            .filter((ch) => ch.text.trim())
            .map((ch) => ({ text: ch.text.trim(), postback_data: (ch.postback_data || ch.text).trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_") })),
        });
        return list;
      }
      case "carousel": {
        const list: any[] = [];
        if (bodyText.trim()) list.push({ type: "text", text: bodyText.trim() });
        list.push({
          type: "carousel",
          text: "Choose one:",
          cards: cards
            .filter((c) => c.title.trim())
            .map((c) => ({
              title: c.title.trim(),
              description: c.description.trim(),
              media_url: c.media_url.trim(),
              actions: [{
                type: "QUICK_REPLY",
                text: (c.chip_text || "Select").trim(),
                postback_data: (c.chip_postback || c.title).trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_"),
              }],
            })),
        });
        return list;
      }
    }
  };

  const handleCreate = async () => {
    if (!name.trim()) { toast.error("Give the template a name."); return; }
    if (!agentId.trim()) {
      toast.error("Enter the RCS Agent ID before creating (e.g. dalmlabs_connect).");
      return;
    }
    const components = buildComponents();
    if (!components || components.length === 0) { toast.error("Add at least one component."); return; }
    setSaving(true);
    try {
      const payload = {
        channel: "RCS",
        category,
        template_name: name.trim().toLowerCase().replace(/\s+/g, "_"),
        rcs_agent_id: agentId,
        language: "en",
        components,
      };
      const [response] = await createTemplate(payload);
      // Backend returns either the created row directly, or {status,data:{...}}
      const created: TemplateModule | undefined = response?.data || response;
      if (!created || !created.template_name) {
        throw new Error(response?.detail || response?.message || "Unknown response shape");
      }
      toast.success(`Template "${created.template_name}" created`);
      onCreated(created);
      reset();
      onClose();
    } catch (err) {
      toast.error(`Create failed: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    // Right-side full-height sidebar. Uses inset-y-0 + right-0 to lock
    // to the full viewport height on every browser (h-full on absolute
    // wasn't reliable without an explicit height parent).
    <div
      className="fixed inset-0 z-50 bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="fixed top-0 right-0 bottom-0 w-full sm:w-[560px] bg-white dark:bg-gray-900 shadow-2xl flex flex-col overflow-hidden"
        style={{ height: "100vh" }}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Create RCS Template</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Auto-approved by Twilio. No manual approval wait.
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Name + Category */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Template name</label>
              <input
                type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Welcome message"
                className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm"
              />
              <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Saved as {name ? name.trim().toLowerCase().replace(/\s+/g, "_") : "example_name"}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Category</label>
              <select
                value={category} onChange={(e) => setCategory(e.target.value as any)}
                className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm"
              >
                <option value="MARKETING">Marketing</option>
                <option value="UTILITY">Utility</option>
                <option value="AUTHENTICATION">Authentication</option>
              </select>
            </div>
          </div>

          {/* RCS Agent — pre-filled from sms.rcs_agents when reachable,
              editable so operator can override or type it in if the
              backend lookup hasn't rolled out yet. */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">RCS Agent ID</label>
            <input
              type="text"
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              placeholder="e.g. dalmlabs_connect"
              className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm font-mono"
            />
            <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">
              Auto-filled from your tenant's rcs_agents row when available. Editable — override for testing.
            </p>
          </div>

          {/* Shape picker */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Template type</label>
            <div className="grid grid-cols-4 gap-2">
              {(["text", "rich_card", "menu", "carousel"] as TemplateShape[]).map((s) => (
                <button
                  key={s}
                  onClick={() => setShape(s)}
                  className={`text-xs py-2 rounded-lg border font-medium capitalize ${
                    shape === s
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-indigo-300"
                  }`}
                >
                  {s === "rich_card" ? "Rich Card" : s}
                </button>
              ))}
            </div>
          </div>

          {/* Message body — every shape uses this */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              {shape === "text" ? "Message" : "Intro text (optional)"}
            </label>
            <textarea
              value={bodyText} onChange={(e) => setBodyText(e.target.value)}
              rows={3}
              placeholder={shape === "text" ? "Hi {{first_name}}, welcome to {{company_name}}." : "Hi {{first_name}}..."}
              className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm"
            />
            <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Variables like <code>{"{{first_name}}"}</code> are filled at send time.</p>
          </div>

          {/* Rich card fields */}
          {shape === "rich_card" && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-2 bg-gray-50 dark:bg-gray-800">
              <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500 dark:text-gray-400">Rich card</p>
              <input
                type="text" value={cardTitle} onChange={(e) => setCardTitle(e.target.value)}
                placeholder="Title (e.g. Your appointment is confirmed)"
                className="w-full border border-gray-200 dark:border-gray-700 rounded px-2 py-1.5 text-sm"
              />
              <input
                type="text" value={cardDesc} onChange={(e) => setCardDesc(e.target.value)}
                placeholder="Description (no {{variables}} here — Twilio disallows them in card subtitles)"
                className="w-full border border-gray-200 dark:border-gray-700 rounded px-2 py-1.5 text-sm"
              />
              <input
                type="text" value={cardImage} onChange={(e) => setCardImage(e.target.value)}
                placeholder="Image URL (https://…)"
                className="w-full border border-gray-200 dark:border-gray-700 rounded px-2 py-1.5 text-sm font-mono"
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text" value={cardButtonText} onChange={(e) => setCardButtonText(e.target.value)}
                  placeholder="Button label (e.g. Learn more)"
                  className="w-full border border-gray-200 dark:border-gray-700 rounded px-2 py-1.5 text-sm"
                />
                <input
                  type="text" value={cardButtonUrl} onChange={(e) => setCardButtonUrl(e.target.value)}
                  placeholder="Button URL (https://…)"
                  className="w-full border border-gray-200 dark:border-gray-700 rounded px-2 py-1.5 text-sm font-mono"
                />
              </div>
            </div>
          )}

          {/* Menu chips */}
          {shape === "menu" && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-2 bg-gray-50 dark:bg-gray-800">
              <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500 dark:text-gray-400">Reply chips (up to 11)</p>
              {chips.map((c, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <input
                    type="text" value={c.text}
                    placeholder="Label"
                    onChange={(e) => setChips(chips.map((x, j) => j === i ? { ...x, text: e.target.value } : x))}
                    className="flex-1 border border-gray-200 dark:border-gray-700 rounded px-2 py-1 text-xs"
                  />
                  <input
                    type="text" value={c.postback_data}
                    placeholder="POSTBACK_KEY"
                    onChange={(e) => setChips(chips.map((x, j) => j === i ? { ...x, postback_data: e.target.value } : x))}
                    className="flex-1 border border-gray-200 dark:border-gray-700 rounded px-2 py-1 text-xs font-mono"
                  />
                  <button onClick={() => setChips(chips.filter((_, j) => j !== i))} className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-rose-600">
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
              <button
                disabled={chips.length >= 11}
                onClick={() => setChips([...chips, { text: "", postback_data: "" }])}
                className="text-[11px] text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
              >
                <Plus size={11} /> Add chip
              </button>
            </div>
          )}

          {/* Carousel cards */}
          {shape === "carousel" && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-3 bg-gray-50 dark:bg-gray-800">
              <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500 dark:text-gray-400">Cards (2–10)</p>
              {cards.map((c, i) => (
                <div key={i} className="border border-gray-200 dark:border-gray-700 rounded p-2 bg-white dark:bg-gray-900 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <p className="text-[10px] font-bold text-[#7B7B7B] font-semibold dark:text-gray-500 flex-1">CARD {i + 1}</p>
                    <button onClick={() => setCards(cards.filter((_, j) => j !== i))} className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-rose-600">
                      <Trash2 size={12} />
                    </button>
                  </div>
                  <input type="text" placeholder="Title" value={c.title}
                    onChange={(e) => setCards(cards.map((x, j) => j === i ? { ...x, title: e.target.value } : x))}
                    className="w-full border border-gray-200 dark:border-gray-700 rounded px-2 py-1 text-xs" />
                  <input type="text" placeholder="Description" value={c.description}
                    onChange={(e) => setCards(cards.map((x, j) => j === i ? { ...x, description: e.target.value } : x))}
                    className="w-full border border-gray-200 dark:border-gray-700 rounded px-2 py-1 text-xs" />
                  <input type="text" placeholder="Image URL" value={c.media_url}
                    onChange={(e) => setCards(cards.map((x, j) => j === i ? { ...x, media_url: e.target.value } : x))}
                    className="w-full border border-gray-200 dark:border-gray-700 rounded px-2 py-1 text-xs font-mono" />
                  <div className="grid grid-cols-2 gap-1.5">
                    <input type="text" placeholder="Chip label" value={c.chip_text}
                      onChange={(e) => setCards(cards.map((x, j) => j === i ? { ...x, chip_text: e.target.value } : x))}
                      className="w-full border border-gray-200 dark:border-gray-700 rounded px-2 py-1 text-xs" />
                    <input type="text" placeholder="POSTBACK_KEY" value={c.chip_postback}
                      onChange={(e) => setCards(cards.map((x, j) => j === i ? { ...x, chip_postback: e.target.value } : x))}
                      className="w-full border border-gray-200 dark:border-gray-700 rounded px-2 py-1 text-xs font-mono" />
                  </div>
                </div>
              ))}
              {cards.length < 10 && (
                <button
                  onClick={() => setCards([...cards, {
                    title: "", description: "", media_url: "",
                    chip_text: "Select", chip_postback: `SELECT_${cards.length + 1}`,
                  }])}
                  className="text-[11px] text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                >
                  <Plus size={11} /> Add card
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <a
            href="https://www.twilio.com/docs/content/quickstart" target="_blank" rel="noreferrer"
            className="text-[11px] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 flex items-center gap-1"
          >
            Twilio Content docs <ExternalLink size={10} />
          </a>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
              Cancel
            </button>
            <button
              onClick={handleCreate} disabled={saving || !name.trim()}
              className="px-4 py-1.5 bg-indigo-600 text-white text-sm rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-40"
            >
              {saving ? "Creating…" : "Create template"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
